import React, { useRef, useState } from 'react';
import {
    Box, Grid, Stack, Typography, IconButton, Tooltip,
    ToggleButtonGroup, ToggleButton, Divider, Popover,
    InputBase, alpha, useTheme
} from '@mui/material';
import { AddRounded, DeleteRounded, FormatColorFillRounded } from '@mui/icons-material';
import Iconify from '../../Iconify';

// ── Visual sub-components ──────────────────────────────────────────────────────

const CircleProgress = ({ value, max = 100, size = 90, color, isSiteBuilder = false, onSave }) => {
    const pct = Math.min(100, Math.max(0, (value / max) * 100));
    const r = (size - 12) / 2;
    const circ = 2 * Math.PI * r;
    const dash = (pct / 100) * circ;
    return (
        <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
            <svg width={size} height={size} style={{ display: 'block' }}>
                <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={alpha(color, 0.15)} strokeWidth={9} />
                <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={9}
                    strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
                    transform={`rotate(-90 ${size / 2} ${size / 2})`} />
            </svg>
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography
                    component="div"
                    fontWeight={800}
                    contentEditable={isSiteBuilder}
                    suppressContentEditableWarning
                    onBlur={(e) => isSiteBuilder && onSave && onSave(Number(e.currentTarget.innerText.trim()) || 0)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            onSave && onSave(Number(e.currentTarget.innerText.trim()) || 0);
                            e.currentTarget.blur();
                        }
                    }}
                    onClick={(e) => isSiteBuilder && e.stopPropagation()}
                        sx={{
                        fontSize: '1.1rem', color,
                        outline: 'none',
                        cursor: isSiteBuilder ? 'text' : 'default',
                        minWidth: 20, textAlign: 'center',
                    }}
                >
                    {value}
                </Typography>
            </Box>
        </Box>
    );
};

const StarsDisplay = ({ value, max = 5, color }) => (
    <Stack direction="row" spacing={0.3}>
        {Array.from({ length: max }).map((_, i) => {
            const filled = i < Math.floor(value);
            const half = !filled && i + 0.5 <= value;
            return (
                <Iconify key={i}
                    icon={filled ? 'eva:star-fill' : (half ? 'eva:star-half-fill' : 'eva:star-outline')}
                    sx={{ width: 18, height: 18, color: filled || half ? color : alpha(color, 0.25) }}
                />
            );
        })}
    </Stack>
);

const DotsDisplay = ({ value, max = 100, color }) => {
    const filled = Math.round((value / max) * 100);
    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '3px', width: (8 + 3) * 10 }}>
            {Array.from({ length: 100 }).map((_, i) => (
                <Box key={i} sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: i < filled ? color : alpha(color, 0.15) }} />
            ))}
        </Box>
    );
};

// ── Display type definitions ───────────────────────────────────────────────────

const DISPLAY_TYPES = [
    { value: 'number',  label: 'Number',   icon: 'eva:hash-fill' },
    { value: 'circle',  label: 'Circle',   icon: 'eva:radio-button-off-fill' },
    { value: 'bar',     label: 'Bar',      icon: 'eva:minus-fill' },
    { value: 'stars',   label: 'Stars',    icon: 'eva:star-fill' },
    { value: 'dots',    label: 'Dots',     icon: 'eva:grid-fill' },
    { value: 'thinbar', label: 'Thin Bar', icon: 'eva:minus-outline' },
];

// ── Editable helpers ───────────────────────────────────────────────────────────

const normalizeEditableHtml = (html) => {
    if (!html) return '';
    let res = html.trim();
    if (res === '<br>' || res === '<p><br></p>') return '';
    return res;
};

const EditableNumber = ({ value, isSiteBuilder, color, variant = 'h3', onSave, sx = {} }) => (
    <Typography
        component="div"
        variant={variant}
        fontWeight={900}
        contentEditable={isSiteBuilder}
        suppressContentEditableWarning
        onBlur={(e) => isSiteBuilder && onSave(Number(e.currentTarget.innerText.trim()) || 0)}
        onKeyDown={(e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                onSave && onSave(Number(e.currentTarget.innerText.trim()) || 0);
                e.currentTarget.blur();
            }
        }}
        sx={{ outline: 'none', cursor: isSiteBuilder ? 'text' : 'default', color, lineHeight: 1, display: 'inline-block', minWidth: 20, ...sx }}
        dangerouslySetInnerHTML={{ __html: typeof value === 'string' ? value : String(value ?? '') }}
    />
);

const EditableText = ({ value, isSiteBuilder, onSave, variant = 'subtitle2', fontWeight = 700, color = 'text.primary', sx = {} }) => (
    <Typography
        component="div"
        variant={variant}
        fontWeight={fontWeight}
        contentEditable={isSiteBuilder}
        suppressContentEditableWarning
        onBlur={(e) => isSiteBuilder && onSave(normalizeEditableHtml(e.currentTarget.innerHTML))}
        onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                onSave && onSave(normalizeEditableHtml(e.currentTarget.innerHTML));
                e.currentTarget.blur();
            }
        }}
        sx={{ outline: 'none', cursor: isSiteBuilder ? 'text' : 'default', color, ...sx }}
        dangerouslySetInnerHTML={{ __html: typeof value === 'string' ? value : String(value ?? '') }}
    />
);

// ── Main component ─────────────────────────────────────────────────────────────

const CommonStatGrid = ({
    stats = [],
    displayType: displayTypeProp = 'number',
    statCols: statColsProp = 2,
    accentColor: accentColorProp,
    isSiteBuilder = false,
    isActive = false,
    onUpdateStats,
    onUpdateBlock,
}) => {
    const theme = useTheme();

    // Local state for immediate visual feedback — syncs from props when they change externally
    const [displayType, setDisplayType] = useState(displayTypeProp);
    const [statCols, setStatCols] = useState(statColsProp);
    const [accentColor, setAccentColor] = useState(accentColorProp);

    React.useEffect(() => { setDisplayType(displayTypeProp); }, [displayTypeProp]);
    React.useEffect(() => { setStatCols(statColsProp); }, [statColsProp]);
    React.useEffect(() => { setAccentColor(accentColorProp); }, [accentColorProp]);

    const color = accentColor || theme.palette.primary.main;
    const [colorAnchor, setColorAnchor] = useState(null);
    const colorBtnRef = useRef(null);

    const handleUpdateStat = (id, field, val) =>
        onUpdateStats(stats.map(s => String(s.id) === String(id) ? { ...s, [field]: val } : s));

    const handleAddStat = () =>
        onUpdateStats([...stats, { id: Date.now(), value: 75, maxValue: 100, label: 'Score', description: 'Description' }]);

    const handleDeleteStat = (id) =>
        onUpdateStats(stats.filter(s => String(s.id) !== String(id)));

    // ── Render one stat card's visual ─────────────────────────────────────────

    const renderVisual = (stat) => {
        const pct = Math.min(100, Math.max(0, (stat.value / (stat.maxValue || 100)) * 100));

        switch (displayType) {
            case 'circle':
                return (
                    <Stack spacing={1.5} alignItems="center" sx={{ textAlign: 'center' }}>
                        <CircleProgress value={stat.value} max={stat.maxValue || 100} size={90} color={color}
                            isSiteBuilder={isSiteBuilder} onSave={(v) => handleUpdateStat(stat.id, 'value', v)} />
                        <Stack spacing={0.25} alignItems="center">
                            <EditableText value={stat.label} isSiteBuilder={isSiteBuilder} variant="subtitle2" fontWeight={700}
                                onSave={(v) => handleUpdateStat(stat.id, 'label', v)} />
                            <EditableText value={stat.description} isSiteBuilder={isSiteBuilder} variant="caption" color="text.secondary"
                                onSave={(v) => handleUpdateStat(stat.id, 'description', v)} />
                        </Stack>
                    </Stack>
                );

            case 'bar':
                return (
                    <Stack spacing={1.5}>
                        <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                            <EditableNumber value={stat.value} isSiteBuilder={isSiteBuilder} color={color} variant="h4"
                                onSave={(v) => handleUpdateStat(stat.id, 'value', v)} />
                        </Stack>
                        <Box sx={{ width: '100%', height: 12, borderRadius: 6, bgcolor: alpha(color, 0.15), overflow: 'hidden' }}>
                            <Box sx={{ width: `${pct}%`, height: '100%', borderRadius: 6, bgcolor: color, transition: 'width 0.4s' }} />
                        </Box>
                        <Stack spacing={0.25}>
                            <EditableText value={stat.label} isSiteBuilder={isSiteBuilder} variant="subtitle2" fontWeight={700}
                                onSave={(v) => handleUpdateStat(stat.id, 'label', v)} />
                            <EditableText value={stat.description} isSiteBuilder={isSiteBuilder} variant="caption" color="text.secondary"
                                onSave={(v) => handleUpdateStat(stat.id, 'description', v)} />
                        </Stack>
                    </Stack>
                );

            case 'stars':
                return (
                    <Stack spacing={1.5}>
                        <Stack direction="row" alignItems="baseline" spacing={1}>
                            <EditableNumber value={stat.value} isSiteBuilder={isSiteBuilder} color={color} variant="h4"
                                onSave={(v) => handleUpdateStat(stat.id, 'value', v)} />
                        </Stack>
                        <StarsDisplay value={stat.value} max={stat.maxValue || 5} color={color} />
                        <Stack spacing={0.25}>
                            <EditableText value={stat.label} isSiteBuilder={isSiteBuilder} variant="subtitle2" fontWeight={700}
                                onSave={(v) => handleUpdateStat(stat.id, 'label', v)} />
                            <EditableText value={stat.description} isSiteBuilder={isSiteBuilder} variant="caption" color="text.secondary"
                                onSave={(v) => handleUpdateStat(stat.id, 'description', v)} />
                        </Stack>
                    </Stack>
                );

            case 'dots':
                return (
                    <Stack spacing={1.5}>
                        <DotsDisplay value={stat.value} max={stat.maxValue || 100} color={color} />
                        <Stack direction="row" alignItems="baseline" spacing={0.5}>
                            <EditableNumber value={stat.value} isSiteBuilder={isSiteBuilder} color={color} variant="h5"
                                onSave={(v) => handleUpdateStat(stat.id, 'value', v)} />
                        </Stack>
                        <Stack spacing={0.25}>
                            <EditableText value={stat.label} isSiteBuilder={isSiteBuilder} variant="subtitle2" fontWeight={700}
                                onSave={(v) => handleUpdateStat(stat.id, 'label', v)} />
                            <EditableText value={stat.description} isSiteBuilder={isSiteBuilder} variant="caption" color="text.secondary"
                                onSave={(v) => handleUpdateStat(stat.id, 'description', v)} />
                        </Stack>
                    </Stack>
                );

            case 'thinbar':
                return (
                    <Stack spacing={1}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <EditableText value={stat.label} isSiteBuilder={isSiteBuilder} variant="body2" fontWeight={700}
                                onSave={(v) => handleUpdateStat(stat.id, 'label', v)} />
                            <EditableNumber value={stat.value} isSiteBuilder={isSiteBuilder} color={color} variant="body2"
                                sx={{ fontWeight: 800 }} onSave={(v) => handleUpdateStat(stat.id, 'value', v)} />
                        </Stack>
                        <Box sx={{ width: '100%', height: 6, borderRadius: 3, bgcolor: alpha(color, 0.15), overflow: 'hidden' }}>
                            <Box sx={{ width: `${pct}%`, height: '100%', borderRadius: 3, bgcolor: color, transition: 'width 0.4s' }} />
                        </Box>
                        <EditableText value={stat.description} isSiteBuilder={isSiteBuilder} variant="caption" color="text.secondary"
                            onSave={(v) => handleUpdateStat(stat.id, 'description', v)} />
                    </Stack>
                );

            case 'number':
            default:
                return (
                    <Stack spacing={0.75}>
                        <EditableNumber value={stat.value} isSiteBuilder={isSiteBuilder} color={color} variant="h3"
                            onSave={(v) => handleUpdateStat(stat.id, 'value', v)} />
                        <EditableText value={stat.label} isSiteBuilder={isSiteBuilder} variant="subtitle2" fontWeight={700}
                            onSave={(v) => handleUpdateStat(stat.id, 'label', v)} />
                        <EditableText value={stat.description} isSiteBuilder={isSiteBuilder} variant="caption" color="text.secondary"
                            onSave={(v) => handleUpdateStat(stat.id, 'description', v)} />
                    </Stack>
                );
        }
    };


    const gridMd = Math.floor(12 / statCols);

    return (
        <Box sx={{ width: '100%', my: 2, position: 'relative' }}>

            {/* ── Block-level toolbar ───────────────────────────────────────── */}
            {isSiteBuilder && isActive && (
                <Stack
                    direction="row" spacing={0.5} alignItems="center"
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                        position: 'absolute', top: -48, left: '50%', transform: 'translateX(-50%)',
                        zIndex: 1000, p: 0.5, bgcolor: 'background.paper',
                        boxShadow: 20, borderRadius: 1, border: '1px solid', borderColor: 'divider',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {/* Display type buttons */}
                    {DISPLAY_TYPES.map(dt => (
                        <Tooltip key={dt.value} title={dt.label}>
                            <IconButton
                                size="small"
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setDisplayType(dt.value);
                                    onUpdateBlock({ displayType: dt.value });
                                }}
                                sx={{
                                    borderRadius: 0.5,
                                    bgcolor: displayType === dt.value ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
                                    color: displayType === dt.value ? 'primary.main' : 'inherit',
                                }}
                            >
                                <Iconify icon={dt.icon} sx={{ width: 15, height: 15 }} />
                            </IconButton>
                        </Tooltip>
                    ))}

                    <Divider orientation="vertical" flexItem />

                    {/* Column buttons */}
                    {['2', '3', '4'].map(c => (
                        <Tooltip key={c} title={`${c} columns`}>
                            <IconButton
                                size="small"
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setStatCols(Number(c));
                                    onUpdateBlock({ statCols: Number(c) });
                                }}
                                sx={{
                                    borderRadius: 0.5,
                                    bgcolor: statCols === Number(c) ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
                                    color: statCols === Number(c) ? 'primary.main' : 'inherit',
                                    minWidth: 28,
                                }}
                            >
                                <Typography variant="caption" fontWeight={700}>{c}</Typography>
                            </IconButton>
                        </Tooltip>
                    ))}

                    <Divider orientation="vertical" flexItem />

                    <Tooltip title="Accent Color">
                        <IconButton ref={colorBtnRef} size="small"
                            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setColorAnchor(colorBtnRef.current); }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                                <FormatColorFillRounded fontSize="small" />
                                <Box sx={{ width: 14, height: 3, borderRadius: '2px', bgcolor: color }} />
                            </Box>
                        </IconButton>
                    </Tooltip>

                    <Popover open={Boolean(colorAnchor)} anchorEl={colorAnchor}
                        onClose={() => setColorAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        onClick={(e) => e.stopPropagation()} sx={{ zIndex: 9999 }}>
                        <Stack spacing={1} sx={{ p: 1.5, minWidth: 160 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>Accent Color</Typography>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <input type="color" value={color}
                                    onChange={(e) => { setAccentColor(e.target.value); onUpdateBlock({ accentColor: e.target.value }); }}
                                    style={{ width: 36, height: 36, border: 'none', borderRadius: 4, cursor: 'pointer', padding: 2 }} />
                                <InputBase value={color}
                                    onChange={(e) => { setAccentColor(e.target.value); onUpdateBlock({ accentColor: e.target.value }); }}
                                    inputProps={{ maxLength: 7, style: { fontFamily: 'monospace', fontSize: 13 } }}
                                    sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1, flex: 1, height: 36 }} />
                            </Stack>
                        </Stack>
                    </Popover>
                </Stack>
            )}

            {/* ── Stat cards grid ───────────────────────────────────────────── */}
            <Grid container spacing={displayType === 'thinbar' ? 1.5 : 3}>
                {stats.map((stat) => (
                    <Grid item xs={12} sm={statCols === 1 ? 12 : 6} md={gridMd} key={stat.id}>
                        <Box
                            className="stat-card"
                            sx={{
                                p: displayType === 'thinbar' ? 2 : 3,
                                borderRadius: 2,
                                bgcolor: alpha(theme.palette.background.paper, 0.85),
                                border: '1px solid', borderColor: 'divider',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                                position: 'relative',
                                transition: 'all 0.2s',
                                '&:hover .stat-del': { opacity: 1 },
                            }}
                        >
                            {isSiteBuilder && (
                                <IconButton className="stat-del" size="small" color="error"
                                    onClick={(e) => { e.stopPropagation(); handleDeleteStat(stat.id); }}
                                    sx={{ position: 'absolute', top: 6, right: 6, opacity: 0, transition: '0.15s', bgcolor: alpha(theme.palette.background.paper, 0.9), zIndex: 2 }}>
                                    <DeleteRounded sx={{ fontSize: 14 }} />
                                </IconButton>
                            )}

                            {renderVisual(stat)}
                        </Box>
                    </Grid>
                ))}

                {isSiteBuilder && (
                    <Grid item xs={12} sm={6} md={gridMd}>
                        <Box onClick={(e) => { e.stopPropagation(); handleAddStat(); }}
                            sx={{
                                height: '100%', minHeight: 100,
                                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                border: '2px dashed', borderColor: 'divider', borderRadius: 2,
                                cursor: 'pointer', color: 'text.secondary', transition: 'all 0.2s',
                                '&:hover': { borderColor: 'primary.main', color: 'primary.main', bgcolor: 'rgba(0,0,0,0.02)' }
                            }}>
                            <AddRounded />
                            <Typography variant="caption" sx={{ mt: 0.5 }}>Add Stat</Typography>
                        </Box>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export default CommonStatGrid;
